import { useMemo } from "react";
import {
  AlertTriangle,
  Check,
  LineChart,
  ShieldCheck,
  Upload,
  X,
} from "lucide-react";
import {
  buildGovernanceActions,
  buildGovernanceHotspots,
  buildGovernanceMatrix,
  buildGovernanceReviewTable,
  buildGovernanceSnapshotCards,
  buildQualityCompositionBars,
  buildRiskHeroStats,
  fmt,
} from "@/utils/dropboxDerived";
import "./DropboxRiskGovernanceTab.css";
import { toTitleCase } from "../utils/toTitleCase";

const SNAPSHOT_ICONS = {
  valid: Check,
  errors: X,
  pending: Upload,
  dropout: LineChart,
};

const ACTION_ICONS = {
  red: AlertTriangle,
  orange: Upload,
  purple: X,
  green: ShieldCheck,
};

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const DropboxRiskGovernanceTab = ({ overview = {}, dataQuality = {}, dropoutHotspots = [] }) => {
  const heroStats = useMemo(
    () => buildRiskHeroStats(overview, dataQuality, dropoutHotspots),
    [overview, dataQuality, dropoutHotspots]
  );
  const snapshotCards = useMemo(
    () => buildGovernanceSnapshotCards(overview, dataQuality, dropoutHotspots),
    [overview, dataQuality, dropoutHotspots]
  );
  const compositionBars = useMemo(
    () => buildQualityCompositionBars(overview, dataQuality),
    [overview, dataQuality]
  );
  const matrixCells = useMemo(
    () => buildGovernanceMatrix(overview, dataQuality),
    [overview, dataQuality]
  );
  const hotspots = useMemo(() => buildGovernanceHotspots(dropoutHotspots, 10), [dropoutHotspots]);
  const reviewTable = useMemo(() => buildGovernanceReviewTable(dropoutHotspots, 10), [dropoutHotspots]);
  const actions = useMemo(
    () => buildGovernanceActions(overview, dataQuality, dropoutHotspots),
    [overview, dataQuality, dropoutHotspots]
  );

  return (
    <div className="dbx-governance-tab" data-testid="dropbox-risk-governance">
      <section className="dbx-hero-card" id="dbx-governance-hero">
        <div className="dbx-risk-hero">
          <div>
            <div className="dbx-risk-main">
              <div className="dbx-risk-gauge" style={heroStats.gaugeStyle} aria-label={`Valid Data ${heroStats.validPct} percent`}>
                <div>
                  <strong>{heroStats.validPct}%</strong>
                  <span>Valid Data</span>
                </div>
              </div>
              <div className="dbx-risk-copy">
                <h2 className="dashboard-kpi-hero-title">Risk & Governance Command Center</h2>
                <p>{heroStats.narrative}</p>
                <div className="dbx-status-row">
                  {heroStats.chips.map((chip) => (
                    <span key={chip.text} className={`dbx-status-chip ${chip.className}`.trim()}>
                      {chip.text}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="dbx-summary-note">
              <div className="dbx-icon-box red">
                <AlertTriangle size={22} />
              </div>
              <div>
                <b>Primary governance action</b>
                <span>
                  Freeze hotspot list for weekly review, assign block owners, and close wrong/duplicate
                  records before final certification.
                </span>
              </div>
            </div>
          </div>

          <div className="dbx-risk-metric-grid" aria-label="Risk KPIs">
            {heroStats.kpis.map((card) => (
              <article key={card.label} className={`dbx-risk-metric-card ${card.cardClass}`.trim()}>
                <small>{card.label}</small>
                <strong>{card.value}</strong>
                <span>{card.sub}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="dbx-section-title" id="dbx-snapshot">
        <div>
          <h2>Governance Snapshot</h2>
          <p>
            Four concise cards make the risk posture scannable before the user enters charts and
            block-level details.
          </p>
        </div>
        <button type="button" className="dbx-section-action" onClick={() => scrollToId("dbx-hotspots")}>
          Review hotspots ↓
        </button>
      </div>

      <section className="dbx-governance-card-grid" aria-label="Governance snapshot">
        {snapshotCards.map((card) => {
          const Icon = SNAPSHOT_ICONS[card.id] ?? Check;
          return (
            <article key={card.id} className="dbx-governance-card">
              <div className="dbx-governance-card-head">
                <div className={`dbx-icon-box ${card.iconClass}`.trim()}>
                  <Icon size={22} />
                </div>
                <span className={`dbx-status-dot ${card.dotClass}`.trim()} />
              </div>
              <h3>{toTitleCase(card.title)}</h3>
              <div className="dbx-governance-score">
                <strong>{card.value}</strong>
                <span>{card.unit}</span>
              </div>
              <div className="dbx-progress-line">
                <i style={{ width: `${card.barWidth}%`, background: card.barColor }} />
              </div>
              <div className="dbx-governance-meta">
                <span>{card.metaLeft}</span>
                <span>{card.metaRight}</span>
              </div>
            </article>
          );
        })}
      </section>

      <div className="dbx-section-title" id="dbx-diagnostics">
        <div>
          <h2>Risk Diagnostics</h2>
          <p>
            Separate the data-quality risk from dropout and transition risks so district users know
            where governance action is required.
          </p>
        </div>
        <button type="button" className="dbx-section-action" onClick={() => scrollToId("dbx-governance-actions")}>
          Go to actions →
        </button>
      </div>

      <section className="dbx-governance-analytics-grid">
        <article className="dbx-panel">
          <div className="dbx-panel-head">
            <div>
              <h3>Data-Quality Risk Composition</h3>
              <p>Current exposure from wrong entries, pending imports and valid records.</p>
            </div>
            <span className="dbx-pill">Quality mix</span>
          </div>
          <div className="dbx-governance-bar-list" aria-label="Data quality risk composition">
            {compositionBars.map((row) => (
              <div key={row.label} className="dbx-governance-hbar-row">
                <div className="dbx-governance-hbar-label">{row.label}</div>
                <div className="dbx-governance-hbar-track">
                  <div
                    className={`dbx-governance-hbar ${row.barClass}`.trim()}
                    style={{ width: `${row.widthPct}%` }}
                  />
                </div>
                <div className="dbx-governance-hbar-value">{row.displayValue}</div>
              </div>
            ))}
          </div>
        </article>

        <article className="dbx-panel">
          <div className="dbx-panel-head">
            <div>
              <h3>Governance Risk Matrix</h3>
              <p>Prioritization combines data quality, dropout volume and death remarks.</p>
            </div>
            <span className="dbx-pill">Decision grid</span>
          </div>
          <div className="dbx-governance-matrix">
            {matrixCells.map((cell) => (
              <div key={cell.title} className={`dbx-governance-matrix-cell ${cell.cellClass}`.trim()}>
                <b>{cell.title}</b>
                <small>{cell.body}</small>
                <strong>{cell.value}</strong>
              </div>
            ))}
          </div>
        </article>
      </section>

      <div className="dbx-section-title" id="dbx-hotspots">
        <div>
          <h2>Dropout Hotspot Watchlist</h2>
          <p>
            Blocks are ranked by dropout count and paired with death remarks, schools covered and
            dropout intensity.
          </p>
        </div>
        <button type="button" className="dbx-section-action" onClick={() => scrollToId("dbx-governance-table")}>
          View detailed table →
        </button>
      </div>

      <section className="dbx-panel">
        <div className="dbx-panel-head">
          <div>
            <h3>Top 10 Block Risks</h3>
            <p>Use this list for weekly retention, evidence and governance reviews.</p>
          </div>
          <span className="dbx-pill">Immediate attention</span>
        </div>
        <div className="dbx-hotspot-list" aria-label="Top block dropout risks">
          {hotspots.map((row) => (
            <div key={row.block.block_name || row.rank} className={`dbx-hotspot-row ${row.isRed ? "red" : ""}`.trim()}>
              <div className="dbx-hotspot-rank">{row.rank}</div>
              <div className="dbx-hotspot-title">
                <b>{row.blockLabel}</b>
                <span>{row.subtitle}</span>
              </div>
              <div className="dbx-hotspot-metric">
                <b>{fmt(row.dropout)} dropouts</b>
                <span>{row.metricLabel}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="dbx-section-title" id="dbx-governance-table">
        <div>
          <h2>Governance Review Table</h2>
          <p>
            Detailed block-level table keeps the operational evidence available without making it the
            first thing users see.
          </p>
        </div>
        <button type="button" className="dbx-section-action" onClick={() => scrollToId("dbx-governance-hero")}>
          Back to top ↑
        </button>
      </div>

      <section className="dbx-governance-two-column" id="dbx-governance-actions">
        <article className="dbx-panel dbx-table-card">
          <div className="dbx-table-head">
            <div>
              <h3>Block-Wise Dropout and Governance Priority</h3>
              <p>Top hotspots requiring weekly intervention tracking.</p>
            </div>
            <span className="dbx-pill">Top 10</span>
          </div>
          <div className="dbx-governance-table-scroll">
            <table className="dbx-overview-table dbx-governance-table" aria-label="Block-wise dropout hotspot review">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Block</th>
                  <th>Schools</th>
                  <th>Dropouts</th>
                  <th>Dropout / School</th>
                  <th>Deaths</th>
                  <th>Priority</th>
                  <th>Owner Action</th>
                </tr>
              </thead>
              <tbody>
                {reviewTable.map((row) => (
                  <tr key={row.block.block_name || row.rank}>
                    <td>{row.rank}</td>
                    <td>{row.blockLabel}</td>
                    <td>{fmt(row.block.total_schools)}</td>
                    <td className="dbx-score">{fmt(row.block.dropout)}</td>
                    <td>{row.block.dropout_density}</td>
                    <td>{fmt(row.block.due_to_death)}</td>
                    <td>
                      <span className={`dbx-tag ${row.priority.tagClass}`.trim()}>{row.priority.label}</span>
                    </td>
                    <td>{row.ownerAction}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>

        <article className="dbx-panel">
          <div className="dbx-panel-head">
            <div>
              <h3>Recommended Governance Queue</h3>
              <p>Focused, officer-ready actions from the risk signals.</p>
            </div>
            <span className="dbx-pill">Priority</span>
          </div>
          <div className="dbx-governance-action-card">
            {actions.items.map((item) => {
              const Icon = ACTION_ICONS[item.iconClass] ?? AlertTriangle;
              return (
                <div key={item.title} className="dbx-governance-action-item">
                  <div className={`dbx-icon-box ${item.iconClass}`.trim()}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.body}</p>
                  </div>
                </div>
              );
            })}
            <div className="dbx-action-footer">
              <b>Next review recommendation</b>
              <span>{actions.footer}</span>
            </div>
          </div>
        </article>
      </section>
    </div>
  );
};

export default DropboxRiskGovernanceTab;
